import type {FC} from 'react'
import type { QueryParams} from '../helpers/ajax';
import {encodeParam, resolveRoute} from '../helpers/ajax'

/**
 * @see https://developers.google.com/maps/documentation/maps-static/start#ImageFormats
 */
type ImageFormat = 'png' | 'png8' | 'png32' | 'gif' | 'jpg' | 'jpg-baseline'

/**
 * @see https://developers.google.com/maps/documentation/maps-static/start#MapTypes
 */
type MapType = 'roadmap' | 'satellite' | 'terrain' | 'hybrid'

export type StaticGoogleMapProps = {
    width: number
    height: number
    scale?: number
    format?: ImageFormat
    mapType?: MapType
    language?: string
    region?: string
} & {
    markers: Marker[]
}

export type MarkerLocation = `${number},${number}` | string | google.maps.LatLngLiteral | [lat: number, lng: number]

/**
 * @see https://developers.google.com/maps/documentation/maps-static/start#Markers
 */
export interface Marker {
    style?: MarkerStyle
    location: MarkerLocation
}

export type MarkerSize = 'tiny' | 'mid' | 'small'
export type PredefinedMarkerColor =
    'black'
    | 'brown'
    | 'green'
    | 'purple'
    | 'yellow'
    | 'blue'
    | 'gray'
    | 'orange'
    | 'red'
    | 'white'
export type AnchorPoint =
    'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'center'
    | 'topleft'
    | 'topright'
    | 'bottomleft'
    | 'bottomright'


/**
 * @see https://developers.google.com/maps/documentation/maps-static/start#Markers
 */
export interface MarkerStyle {
    size?: MarkerSize
    color?: string | PredefinedMarkerColor  // `0x${string}`
    label?: string
    icon?: string
    anchorPoint?: `${number},${number}` | AnchorPoint
}

const encoder = new TextEncoder()

function percentEncodeString(x: string): string {
    return Array.from(encoder.encode(x)).map(i => '%'+i.toString(16).padStart(2,'0')).join('')
}

function percentEncodeRegex(x: string, re:RegExp): string {
    return String(x).replace(re, m => percentEncodeString(m[0]))
}

function serializeObject(params: Record<string, any>): string {
    return Object.keys(params).filter(k => params[k] !== undefined).sort()
        .map(k => `${percentEncodeRegex(k,/:+/g)}:${percentEncodeRegex(params[k],/\|+/g)}`).join('|')
    // return Object.keys(params).filter(k => params[k] !== undefined).sort().map(k => `${k}:${params[k]}`).join('|')
}

function locToStr(loc: MarkerLocation) {
    if (typeof loc === 'string') {
        return loc
    }
    if (Array.isArray(loc)) {
        return `${loc[0]},${loc[1]}`
    }
    return `${loc.lat},${loc.lng}`
}

function queryParams(params: QueryParams) {
    return Object.keys(params)
        .filter(k => params[k] !== undefined)
        .sort()
        .map(k => {
            const p = params[k]
            if (Array.isArray(p)) {
                return p
                    .map(val => `${encodeParam(k)}=${encodeParam(val)}`)
                    .join('&')
            }
            return `${encodeParam(k)}=${encodeParam(p)}`
        })
        .join('&')
}

// TODO: add directions: https://stackoverflow.com/a/38574451/65387

const StaticGoogleMap: FC<StaticGoogleMapProps> = props => {

    const params: Record<string, any> = {
        size: `${props.width}x${props.height}`,
        scale: props.scale,
        format: props.format,
        maptype: props.mapType,
        language: props.language,
        region: props.region,
        // key: globalThis.env.GOOGLE_MAPS_API_KEY,
    }

    const baseUrl = resolveRoute('staticGoogleMap')+'?';

    if (props.markers) {
        const markerMap = new Map<string, string[]>()
        // Use just the last 15 markers. Google Maps imposes this limit.
        for (const marker of props.markers.slice(-15)) {
            const style = marker.style ? serializeObject({
                size: marker.style.size,
                color: marker.style.color,
                label: marker.style.label,
                icon: marker.style.icon,
                anchor: marker.style.anchorPoint,
            }) : ''
            // console.log(marker.style.icon)
            const location = locToStr(marker.location)
            if (markerMap.has(style)) {
                markerMap.get(style)!.push(location)
            } else {
                markerMap.set(style, [location])
            }
        }

        params.markers = [];
        for(const [style,locations] of markerMap) {
            params.markers.push(`${style}|${locations.join('|')}`)
            // 4000 chars is soft cap imposed by GoogleMapsController
            // Too long URLs also break php-fpm and/or nginx
            if((baseUrl+queryParams(params)).length > 4000) {
                params.markers.pop();
                break;
            }
        }
    }
    // logJson(params)

    // const url = 'https://maps.googleapis.com/maps/api/staticmap?' + queryParams(params)
    // const url = resolveRoute('staticGoogleMap',{params:queryParams(params)});
    const url = baseUrl+queryParams(params);

    return <img alt="Google Map" width={props.width} height={props.height} src={url} style={{backgroundColor: '#E8E8E8',maxWidth:'100%',width:'auto',height:'auto'}}/>
}

export default StaticGoogleMap
